import { Dynamic } from "solid-js/web";
import { cn } from "@apunte-digital/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
  /**
   * Wordmark element. By default the large wordmark is an `h1` (it is the
   * page's title brand); pass `"p"` when the logo is DECORATIVE and the real
   * heading lives elsewhere (e.g. the auth brand panel already provides the
   * `h1`, so the panel's logo must be `p` to avoid duplicating landmarks).
   * Also accepts `"span"`.
   */
  as?: "h1" | "p" | "span";
}

/**
 * Apunte Digital brand mark — "premium digital paper".
 *
 * The glyph is a sheet of paper with a folded corner and two writing lines,
 * topped by a feather mark. It evokes notes on paper without resorting to a
 * generic "task" icon.
 *
 * CONTRAST IN ALL THEMES (GymLog-like gotcha): the mark is NOT drawn with
 * `text-accent` on a faint surface (translucent `bg-surface` is almost
 * invisible and `text-accent` disappears in several themes). Instead the glyph
 * lives on a SOLID `bg-accent` chip and is painted with `text-accent-foreground`,
 * a pair of tokens with guaranteed contrast by construction in each shipped
 * theme (Carbon, Platinum, Obsidian, Porcelain, Midnight Aurora, Nordic
 * Dawn, Deep Forest, Meadow Mist, Cyber Neon, Synthwave, Royal Velvet, Lavender
 * Silk, Crimson Ember, Sandstone, Lavender Dream, Cosmic Lavender). The wordmark
 * uses the brand editorial serif (Fraunces) in `text-foreground`, which also
 * contrasts the background in all light/dark modes.
 */
function AppunteMark(props: { class?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class={props.class}
      aria-hidden="true"
    >
      {/* Paper sheet with a folded corner */}
      <path
        d="M8 4h11l5 5v17a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"
        fill="currentColor"
        fill-opacity="0.16"
        stroke="currentColor"
        stroke-width="1.7"
        stroke-linejoin="round"
      />
      <path d="M19 4v5h5" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" />
      {/* Writing lines */}
      <path
        d="M10 16h9M10 20h6"
        stroke="currentColor"
        stroke-width="1.7"
        stroke-linecap="round"
        stroke-opacity="0.7"
      />
      {/* Quill stroke — same currentColor for guaranteed contrast on the chip */}
      <path
        d="M22.5 13.5 16 20l-3 1 1-3 6.5-6.5a1.4 1.4 0 0 1 2 2Z"
        fill="currentColor"
        stroke="currentColor"
        stroke-width="1.3"
        stroke-linejoin="round"
      />
    </svg>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";

  return (
    <div
      class={cn(
        "flex items-center gap-3 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground shadow-sm shadow-accent/25 ring-1 ring-black/5">
            <AppunteMark class="h-6 w-6" />
          </div>
          <Dynamic
            component={props.as ?? "span"}
            class="font-serif text-xl font-semibold tracking-tight text-foreground"
          >
            {/* oxlint-disable lingui/no-unlocalized-strings -- brand name */}
            Apunte Digital
            {/* oxlint-enable lingui/no-unlocalized-strings */}
          </Dynamic>
        </>
      ) : (
        <>
          <div class="mb-1 flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-accent text-accent-foreground shadow-md shadow-accent/30 ring-1 ring-black/5 transition-transform duration-300 hover:scale-105 active:scale-95">
            <AppunteMark class="h-9 w-9" />
          </div>
          <Dynamic
            component={props.as ?? "h1"}
            class="font-serif text-3xl font-semibold tracking-tight text-foreground"
          >
            {/* oxlint-disable lingui/no-unlocalized-strings -- brand name */}
            Apunte Digital
            {/* oxlint-enable lingui/no-unlocalized-strings */}
          </Dynamic>
        </>
      )}
    </div>
  );
}
